import configurators from '~/data/productViewConfigurators';
import {
    ConfiguratorControl,
    ConfiguratorSelections,
} from '~/types/configurator';
import { ProductData } from '~/types/product';

const choiceEnabledCustomLogic = (
    productType: string,
    controlKey: string,
    choiceValue: string | number,
) => {
    if ('custom-printed-sticker' === productType) {
        if ('shape' === controlKey) {
            return true;
        } else if ('size' === controlKey && 'custom' === choiceValue) {
            return true;
        }
    }

    return null;
};

const choiceMatchesAnyProductData = (
    controlKey: string,
    choiceValue: string | number,
    productDatas: ProductData[],
) => {
    let choiceMatches = false;
    productDatas.forEach((productData) => {
        if (!choiceMatches) {
            const attributeValue =
                productData.configuratorAttributes[controlKey];
            if (attributeValue === choiceValue) {
                choiceMatches = true;
            }
        }
    });

    return choiceMatches;
};

const filterProductDatasMatchingSelections = (
    productType: string,
    productDatas: ProductData[],
    selections: ConfiguratorSelections,
) => {
    return productDatas.filter((productData) => {
        const attributes = productData.configuratorAttributes;

        if (!Object.keys(attributes).length) {
            return false;
        }

        let productDataMatchesSelections = true;

        Object.keys(selections).forEach((controlKey) => {
            if (
                productType === 'custom-printed-sticker' &&
                ('shape' === controlKey ||
                    ('size' === controlKey &&
                        selections[controlKey] === 'custom'))
            ) {
                return;
            }

            if (
                selections[controlKey] &&
                selections[controlKey] !== attributes[controlKey]
            ) {
                productDataMatchesSelections = false;
            }
        });

        return productDataMatchesSelections;
    });
};

interface State {
    sku: string | null;
    showErrors: boolean;
    selections: ConfiguratorSelections;
    contents: string | null;
}

const useProductViewConfigurator = async (
    productType: Ref<string>,
    productDatas: Ref<ProductData[]>,
) => {
    const state: State = reactive({
        sku:
            (useRoute().params.sku as string) ||
            useRoute().hash?.replace('#', '').split('&')[0].split('=')[1] ||
            null,
        showErrors: false,
        selections: {},
        contents: null,
    });

    const configurator = computed(() => {
        return configurators[productType.value] || null;
    });

    const isStockedProduct = computed(() => {
        if (!productType.value) {
            return null;
        }

        return (
            !productType.value.includes('custom-printed') &&
            !productType.value.includes('design-lab')
        );
    });

    const isCustomPrintedProduct = computed(() => {
        if (!productType.value) {
            return null;
        }

        return productType.value.includes('custom-printed');
    });

    const isDesignLabProduct = computed(() => {
        if (!productType.value) {
            return null;
        }

        return productType.value.includes('design-lab');
    });

    const showContentsSelect = computed(() => {
        if (!isCustomPrintedProduct.value) {
            return false;
        }

        return ![
            'custom-printed-sticker',
            'custom-printed-beverage-label',
            'custom-printed-bag-label',
        ].includes(productType.value);
    });

    const controls = computed<ConfiguratorControl[]>(() => {
        return (configurator.value?.controls || []).map((control) => {
            const choices = control.choices.map((choice) => {
                let tooltip = null;

                let enabled = choiceEnabledCustomLogic(
                    productType.value,
                    control.key,
                    choice.value,
                );

                if (null === enabled) {
                    const enabledFilterSelections = { ...state.selections };
                    enabledFilterSelections[control.key] = undefined;
                    enabled = choiceMatchesAnyProductData(
                        control.key,
                        choice.value,
                        filterProductDatasMatchingSelections(
                            productType.value,
                            productDatas.value,
                            enabledFilterSelections,
                        ),
                    );
                }

                if (enabled) {
                    const stockFilterSelections = { ...state.selections };
                    stockFilterSelections[control.key] = choice.value;
                    const isInStock =
                        filterProductDatasMatchingSelections(
                            productType.value,
                            productDatas.value,
                            stockFilterSelections,
                        ).filter((productData) => productData.isInStock)
                            .length > 0;

                    if (!isInStock) {
                        tooltip = 'Out of stock';
                        enabled = false;
                    }
                } else {
                    tooltip = 'See available options';
                }

                return {
                    ...choice,
                    disabled: !enabled,
                    tooltip,
                };
            });

            let controlErrorMessage = null;
            const enabledChoices = choices.filter((choice) => !choice.disabled);
            if (enabledChoices.length && !state.selections[control.key]) {
                controlErrorMessage = 'Please select an option.';
            }

            return {
                ...control,
                choices,
                errorMessage: controlErrorMessage,
            };
        });
    });

    const filteredProductDatas = computed<ProductData[]>(() => {
        if (1 === productDatas.value.length && 0 === controls.value.length) {
            return productDatas.value;
        }

        return filterProductDatasMatchingSelections(
            productType.value,
            productDatas.value,
            state.selections,
        );
    });

    const selectedProductData = computed<ProductData | null>(() => {
        if (controls.value.length) {
            const selectionValues = Object.values(state.selections).filter(
                (value) => !!value,
            );
            if (!selectionValues.length) {
                return null;
            }
        }

        for (let i = 0; i < controls.value.length; i++) {
            if (controls.value[i].errorMessage) {
                return null;
            }
        }

        return filteredProductDatas.value.length === 1
            ? filteredProductDatas.value[0]
            : null;
    });

    const validationMessages = computed(() => {
        const messages: string[] = [];

        controls.value.forEach((control) => {
            if (control.errorMessage && !control.hideOnProductPage) {
                messages.push(`Missing ${control.keyLabel}`);
            }
        });

        if (!selectedProductData.value && !messages.length) {
            messages.push(
                `Please configure your ${
                    productDatas.value[0]?.productTypeShortLabel || 'product'
                }.`,
            );
        }

        if (showContentsSelect.value && !state.contents) {
            messages.push(
                `Missing ${productDatas.value[0]?.productTypeShortLabel} contents`,
            );
        }

        return messages;
    });

    let initialProductData;
    if (state.sku) {
        initialProductData = productDatas.value.find((productData) => {
            return productData.sku.toLowerCase() === state.sku?.toLowerCase();
        });
        if (!initialProductData) {
            throw createError({
                statusCode: 404,
                statusMessage: 'Page Not Found',
            });
        }
    } else if (productDatas.value.length === 1) {
        initialProductData = productDatas.value[0];
    }
    if (initialProductData) {
        const attributes = initialProductData.configuratorAttributes;
        Object.keys(attributes).forEach((key) => {
            const control = controls.value.find(
                (control) => control.key === key,
            );
            if (control) {
                const choice = control.choices.find(
                    (choice) => choice.value === attributes[key],
                );
                if (choice) {
                    state.selections[key] = attributes[key];
                }
            }
        });
    }

    const deselectInvalidChoices = (
        currentControlKey: string,
        currentValue: number | string,
    ) => {
        const currentChoiceSelections: ConfiguratorSelections = {};
        currentChoiceSelections[currentControlKey] = currentValue;

        controls.value.forEach((control) => {
            if (control.key === currentControlKey) {
                return;
            }

            control.choices.forEach((choice) => {
                if (
                    undefined === state.selections[control.key] ||
                    state.selections[control.key] !== choice.value
                ) {
                    return;
                }

                const matchingInStockProductDatas =
                    filterProductDatasMatchingSelections(
                        productType.value,
                        productDatas.value,
                        currentChoiceSelections,
                    ).filter((productData) => productData.isInStock);

                const choiceEnabled = choiceMatchesAnyProductData(
                    control.key,
                    choice.value,
                    matchingInStockProductDatas,
                );

                if (!choiceEnabled && !control.ignoreForFilter) {
                    state.selections[control.key] = undefined;
                }
            });
        });
    };

    const selectRelatedChoices = () => {
        let reachedCutoff = false;

        controls.value.forEach((control) => {
            if (state.selections[control.key] || reachedCutoff) {
                return;
            }

            const enabledChoices = control.choices.filter(
                (choice) => !choice.disabled,
            );

            if (enabledChoices.length > 1) {
                reachedCutoff = true;
            } else if (enabledChoices.length === 1) {
                state.selections[control.key] = enabledChoices[0].value;
            }
        });
    };

    const onButtonClick = (controlKey: string, value: number | string) => {
        const currentValue = state.selections[controlKey];

        let newValue: number | string | undefined;
        if (currentValue !== value) {
            newValue = value;
        }

        state.selections[controlKey] = newValue;

        if (newValue) {
            deselectInvalidChoices(controlKey, newValue);
            selectRelatedChoices();
        }
    };

    watch(validationMessages, (messages) => {
        if (!messages.length) {
            state.showErrors = false;
        }
    });

    return {
        isStockedProduct,
        isCustomPrintedProduct,
        isDesignLabProduct,
        showContentsSelect,
        filteredProductDatas,
        selectedProductData,
        controls,
        validationMessages,
        onButtonClick,
        state,
    };
};

export default useProductViewConfigurator;
